import type { PlaybookSummaryView } from "@typedoc/contracts/processes/contract";
import { z } from "zod";
import { playbookTarget } from "./page";

// The Playbooks list's rules (decision `2026-11-01-playbooks-list-is-flat-and-drafts-are-private`):
// what a row says, what the filters keep, and where a row leads. The page draws them.

/** The segment: everything, what waits on people, or the person's own drafts. */
export const PLAYBOOK_SHOWS = ["all", "needs-people", "drafts"] as const;
export type PlaybookShow = (typeof PLAYBOOK_SHOWS)[number];

/** The Teammate menu's key for a playbook that has no teammate yet. */
export const NO_TEAMMATE = "none";

/**
 * What the Playbooks address carries, so going back restores the view: the search, the segment and
 * the teammates chosen (none chosen is everyone). A value this build does not know falls back to
 * the default instead of an error page, and a default is left out of the address.
 */
export const playbooksSearchSchema = z.object({
  q: z.string().optional().catch(undefined),
  show: z.enum(["needs-people", "drafts"]).optional().catch(undefined),
  teammate: z.array(z.string()).optional().catch(undefined),
});
export type PlaybooksSearch = z.infer<typeof playbooksSearchSchema>;

/** The Teammate menu's key for a row: its teammate's ID, or "none". */
export const teammateKeyOf = (playbook: PlaybookSummaryView) =>
  playbook.teammate?.id ?? NO_TEAMMATE;

/**
 * The state, in the words the publish review uses. Nothing turns a playbook on or off yet, so a
 * published one says it is published and which version, never "Live" (decision of 1 Nov); the
 * second line is what to know, or null.
 */
export function stateOf(playbook: PlaybookSummaryView): {
  label: string;
  note: string | null;
} {
  if (playbook.standing.kind === "draft") return { label: "Draft", note: null };
  return {
    label: `Published · v${playbook.standing.version}`,
    note: playbook.standing.yourDraft ? "Your draft has changes to publish" : null,
  };
}

/** The line under a playbook's name: what it delivers, or the chat building a draft. */
export function subtitleOf(playbook: PlaybookSummaryView): string | null {
  if (playbook.standing.kind === "draft") {
    return playbook.chat?.title
      ? `Being built in “${playbook.chat.title}”`
      : "Being built in a chat";
  }
  return playbook.delivers.length > 0 ? `Delivers ${joinWords(playbook.delivers)}` : null;
}

/** "a", "a and b", "a, b and c", each in lower case where it starts a sentence's middle. */
function joinWords(words: readonly string[]) {
  const lowered = words.map((word) => word.replace(/^The /, "the ").replace(/^A /, "a "));
  if (lowered.length <= 1) return lowered[0] ?? "";
  return `${lowered.slice(0, -1).join(", ")} and ${lowered.at(-1)}`;
}

/**
 * Where a row leads: the playbook's own page (decision `2026-11-02-a-playbook-opens-on-its-overview-
 * and-changing-docks-the-chat`). A published one opens its Overview, viewing; a draft never
 * published opens changing, on Steps, with the chat building it docked (a new one when the person
 * can't open that chat).
 */
export const openTargetOf = (playbook: PlaybookSummaryView) =>
  playbookTarget({
    id: playbook.id,
    published: playbook.standing.kind === "published",
    chatId: playbook.chat?.id ?? null,
  });

/** The address a row's Copy link copies, on this origin: the playbook's page. */
export function linkOf(origin: string, target: ReturnType<typeof openTargetOf>): string {
  const path = target.to.replace("$playbookId", encodeURIComponent(target.params.playbookId));
  const query = "search" in target ? `?chat=${encodeURIComponent(target.search.chat)}` : "";
  return `${origin}${path}${query}`;
}

/** One choice in the Teammate menu, with how many playbooks it holds. */
export type TeammateChoice = {
  key: string;
  name: string | null;
  count: number;
};

/**
 * The Teammate menu's choices: every teammate a playbook here belongs to, by name, then "No teammate
 * yet". Each counts the playbooks it holds, so a choice that would empty the list says so.
 */
export function teammateChoices(playbooks: readonly PlaybookSummaryView[]): TeammateChoice[] {
  const named = new Map<string, TeammateChoice>();
  let without = 0;
  for (const playbook of playbooks) {
    if (!playbook.teammate) {
      without += 1;
      continue;
    }
    const { id, name } = playbook.teammate;
    const known = named.get(id);
    if (known) known.count += 1;
    else named.set(id, { key: id, name, count: 1 });
  }
  return [
    ...[...named.values()].sort((a, b) =>
      (a.name ?? "").localeCompare(b.name ?? "", "en", { sensitivity: "base" }),
    ),
    { key: NO_TEAMMATE, name: null, count: without },
  ];
}

export type PlaybookQuery = {
  term: string;
  show: PlaybookShow;
  teammates: readonly string[];
};

/** The words a search looks through: what a row shows. */
const searchable = (playbook: PlaybookSummaryView) =>
  [playbook.name, ...playbook.delivers, playbook.teammate?.name ?? "", playbook.chat?.title ?? ""]
    .join(" ")
    .toLowerCase();

/**
 * The rows a search, the segment and the Teammate menu keep, in the list's own order (by name).
 * Nothing records yet what waits on people (ENG-1021), so Needs people keeps none.
 */
export function filterPlaybooks(
  playbooks: readonly PlaybookSummaryView[],
  { term, show, teammates }: PlaybookQuery,
): PlaybookSummaryView[] {
  const needle = term.trim().toLowerCase();
  return playbooks.filter(
    (playbook) =>
      show !== "needs-people" &&
      (show !== "drafts" || playbook.standing.kind === "draft") &&
      (teammates.length === 0 || teammates.includes(teammateKeyOf(playbook))) &&
      (needle === "" || searchable(playbook).includes(needle)),
  );
}

/** The published rows, then the person's drafts under the divider, each in the list's order. */
export function splitByStanding(playbooks: readonly PlaybookSummaryView[]) {
  return {
    published: playbooks.filter((playbook) => playbook.standing.kind === "published"),
    drafts: playbooks.filter((playbook) => playbook.standing.kind === "draft"),
  };
}
